import type { Geometry, Position } from "geojson";

type Point = readonly [longitude: number, latitude: number];

/**
 * Tests Polygon and MultiPolygon geometry, treating an exact boundary point as
 * inside. The conservative boundary behaviour is intentional for legal zones.
 */
export function pointInGeometry(point: Point, geometry: Geometry | null): boolean {
  if (!geometry || (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")) return false;
  const polygons = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  return polygons.some((polygon) => {
    const outerRing = polygon[0];
    return (
      outerRing !== undefined &&
      inRing(point, outerRing) &&
      !polygon.slice(1).some((hole) => inRing(point, hole))
    );
  });
}

/**
 * Approximates the shortest distance to a zone edge in metres. An
 * equirectangular projection is adequate for the short warning distances and UK
 * latitudes involved; this must not be used as survey-grade measurement.
 */
export function distanceToGeometryMetres(point: Point, geometry: Geometry | null): number {
  let nearest = Number.POSITIVE_INFINITY;
  for (const ring of geometryRings(geometry)) {
    for (let index = 1; index < ring.length; index += 1) {
      nearest = Math.min(
        nearest,
        segmentDistance(point, asPoint(ring[index - 1]), asPoint(ring[index])),
      );
    }
  }
  return nearest;
}

/** Returns the latest effective 28-day AIRAC date, never a future cycle. */
export function currentAiracDate(today = new Date(), anchor = "2026-09-03"): string {
  const dayMilliseconds = 86_400_000;
  const cycleMilliseconds = 28 * dayMilliseconds;
  const utcToday = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const anchorTime = Date.parse(`${anchor}T00:00:00Z`);
  const effective =
    anchorTime + Math.floor((utcToday - anchorTime) / cycleMilliseconds) * cycleMilliseconds;
  return new Date(effective).toISOString().slice(0, 10);
}

function inRing([x, y]: Point, ring: Position[]): boolean {
  let inside = false;
  for (
    let index = 0, previous = ring.length - 1;
    index < ring.length;
    previous = index, index += 1
  ) {
    const [x1, y1] = asPoint(ring[index]);
    const [x2, y2] = asPoint(ring[previous]);
    if (pointOnSegment(x, y, x1, y1, x2, y2)) return true;
    if (y1 > y !== y2 > y && x < ((x2 - x1) * (y - y1)) / (y2 - y1) + x1) inside = !inside;
  }
  return inside;
}

function pointOnSegment(
  x: number,
  y: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): boolean {
  const cross = (x - x1) * (y2 - y1) - (y - y1) * (x2 - x1);
  return (
    Math.abs(cross) < 1e-10 &&
    x >= Math.min(x1, x2) &&
    x <= Math.max(x1, x2) &&
    y >= Math.min(y1, y2) &&
    y <= Math.max(y1, y2)
  );
}

function geometryRings(geometry: Geometry | null): Position[][] {
  if (!geometry) return [];
  if (geometry.type === "Polygon") return geometry.coordinates;
  if (geometry.type === "MultiPolygon") return geometry.coordinates.flat();
  return [];
}

function segmentDistance(
  [longitude, latitude]: Point,
  [longitude1, latitude1]: Point,
  [longitude2, latitude2]: Point,
): number {
  const longitudeScale = 111_320 * Math.cos((latitude * Math.PI) / 180);
  const latitudeScale = 110_540;
  const [px, py] = [longitude * longitudeScale, latitude * latitudeScale];
  const [x1, y1] = [longitude1 * longitudeScale, latitude1 * latitudeScale];
  const [x2, y2] = [longitude2 * longitudeScale, latitude2 * latitudeScale];
  const [dx, dy] = [x2 - x1, y2 - y1];
  const fraction = Math.max(
    0,
    Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy || 1)),
  );
  return Math.hypot(px - (x1 + fraction * dx), py - (y1 + fraction * dy));
}

function asPoint(position: Position | undefined): Point {
  const longitude = position?.at(0);
  const latitude = position?.at(1);
  if (longitude === undefined || latitude === undefined)
    throw new Error("Invalid GeoJSON position");
  return [longitude, latitude];
}
